import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

/**
 * History-aware Back (app-foundation web kit).
 *
 * A session-scoped stack mirrors the browser history (pathname + search, so list filters,
 * page and sort travel with each entry). Back then:
 *  - returns to the previous entry WITH its filters (history.go(-n), not a fresh URL), and
 *  - skips entries that were submitted create/edit pages (`markSubmitted()`, or a path ending
 *    in /new, /create or /edit), so Back after "Save" never re-opens a spent form.
 * When there is no usable entry (deep link, new tab) it falls back to the given parent URL.
 */
interface Entry { key: string; path: string; skip?: boolean }

const STORE = 'bigroi.navHistory'
const FORM_PATH = /\/(new|create|edit)(\/|$)/

function load(): Entry[] {
  try { const v = JSON.parse(sessionStorage.getItem(STORE) ?? '[]'); return Array.isArray(v) ? v : [] } catch { return [] }
}
function save(s: Entry[]) {
  try { sessionStorage.setItem(STORE, JSON.stringify(s.slice(-50))) } catch { /* storage blocked: degrade to fallback URLs */ }
}

let stack: Entry[] = typeof window === 'undefined' ? [] : load()

interface LocLike { key: string; pathname: string; search: string }
export function recordNavigation(loc: LocLike, action: 'PUSH' | 'REPLACE' | 'POP') {
  const entry: Entry = { key: loc.key, path: loc.pathname + loc.search }
  if (action === 'REPLACE' && stack.length) {
    const top = stack[stack.length - 1]
    stack[stack.length - 1] = { ...entry, skip: top.skip && top.path.split('?')[0] === loc.pathname }
  } else if (action === 'POP' && loc.key !== 'default') {
    const i = stack.findIndex((e) => e.key === loc.key)
    if (i >= 0) stack = stack.slice(0, i + 1)
    else stack.push(entry)
  } else {
    // A full page load always has key "default": compare the path too, or it is never recorded.
    const top = stack[stack.length - 1]
    if (!top || top.key !== loc.key || top.path !== entry.path) stack.push(entry)
  }
  save(stack)
}

/** Wire once, right after createBrowserRouter. */
export function initNavHistory(router: { state: { location: LocLike; historyAction: string }; subscribe: (fn: (s: { location: LocLike; historyAction: string }) => void) => unknown }) {
  let last = ''
  const on = (s: { location: LocLike; historyAction: string }) => {
    if (s.location.key === last) return
    last = s.location.key
    recordNavigation(s.location, s.historyAction as 'PUSH' | 'REPLACE' | 'POP')
  }
  on(router.state)
  router.subscribe(on)
}

/** The current page is a form that has been submitted: Back must jump over it. */
export function markSubmitted() {
  if (stack.length) { stack[stack.length - 1].skip = true; save(stack) }
}

/** Steps back to the nearest usable entry, or -1 when there is none. */
function backTarget(): { delta: number; path: string } | null {
  for (let i = stack.length - 2; i >= 0; i--) {
    const e = stack[i]
    if (e.skip || FORM_PATH.test(e.path.split('?')[0])) continue
    return { delta: i - (stack.length - 1), path: e.path }
  }
  return null
}

/** Latest visited URL (with filters) for a pathname, e.g. "/employees" → "/employees?status=ACTIVE". */
export function lastVisited(pathname: string): string | null {
  for (let i = stack.length - 2; i >= 0; i--) if (stack[i].path.split('?')[0] === pathname) return stack[i].path
  return null
}

export function useNavHistory() {
  const navigate = useNavigate()
  const goBack = useCallback((fallback: string) => {
    const t = backTarget()
    // Only travel through history when the target is the logical parent or any real page;
    // a fallback route is used for deep links / fresh tabs.
    if (t) navigate(t.delta)
    else navigate(lastVisited(fallback.split('?')[0]) ?? fallback)
  }, [navigate])
  return { goBack, markSubmitted, lastVisited, canGoBack: () => backTarget() !== null }
}
